import { useState } from 'react'

import { usePermissions } from 'commons/customHooks/useAuth'
import { formatDate } from 'commons/utils/date'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { toAllFirstLetterUpper } from 'commons/utils/string'
import {
  ContentTooltip,
  type HeadItemType,
  Icon,
  Pagination,
  SlimButton,
  Table,
  Tooltip,
  Typography,
} from 'components'
import type { ActionPerm } from 'components/Structures/Header/constants'
import { CriticismType, OrderBlockers, TypesValidations, YesNoNotApplicable } from 'constants/enums'
import { setMaterialList } from 'store/ExtraVolumeContext/ExtraVolumeSlice'
import type {
  Cor,
  ExtraMaterial,
  MaterialItensAction,
  TipoCriticaVolume,
} from 'store/ExtraVolumeContext/Types/ExtraVolumeType'
import { useDispatch, useSelector } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import { ModalConfirmation } from '../ModalConfirmation'
import * as S from './styles'

const LIMIT_AMOUNT = 100
const MIN_STOCK_AMOUNT = 0

type VolumeTableProps = {
  payload: ExtraMaterial[]
  handlePageChange: (page: number) => void
  updateTable: () => void
}
interface ActionItems extends MaterialItensAction {
  cores: string[]
}
const VolumeTable = ({ payload, handlePageChange, updateTable }: VolumeTableProps) => {
  const [showModal, setShowModal] = useState(false)
  const [isReject, setIsReject] = useState(false)
  const [critic, setCritic] = useState<TipoCriticaVolume>('')
  const [actionItems, setActionItems] = useState<MaterialItensAction[] | ActionItems[]>([])

  const { pagination, materialsList } = useSelector((store) => store.extraVolume)
  const dispatch = useDispatch()
  const { verifyPermission } = usePermissions()
  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.EXTRA_VOLUME, action)
  }

  const handleConfirmation = (isReject: boolean, item?: ExtraMaterial) => {
    if (item) {
      const actionItens = isReject
        ? { nr_item_pedi_rasc: item.NumeroItem, nr_pedi_rasc: item.NumeroPedido }
        : {
            cores: item.Cores.map((c) => c.Codigo),
            nr_item_pedi_rasc: item.NumeroItem,
            nr_pedi_rasc: item.NumeroPedido,
          }
      setCritic(item.TipoCritica)
      setActionItems([actionItens])
    } else {
      const checked = materialsList.filter((m) => m.checked)
      const critic = checked.some((c) => c.TipoCritica === CriticismType.QUOTA_STOCK)
        ? CriticismType.QUOTA_STOCK
        : checked.some((c) => c.TipoCritica === CriticismType.STOCK)
          ? CriticismType.STOCK
          : CriticismType.QUOTA
      setCritic(critic)
      const arrItens = isReject
        ? checked.map((i) => ({
            nr_item_pedi_rasc: i.NumeroItem,
            nr_pedi_rasc: i.NumeroPedido,
          }))
        : checked.map((i) => ({
            cores: i.Cores.map((c) => c.Codigo),
            nr_item_pedi_rasc: i.NumeroItem,
            nr_pedi_rasc: i.NumeroPedido,
          }))
      setActionItems(arrItens)
    }
    setIsReject(isReject)
    setShowModal(true)
  }
  const handleConfirmationAction = () => {
    setShowModal(false)
    updateTable()
  }

  const getCheckedExtraVolume = () => {
    return materialsList.filter((item) => item.checked)
  }

  const BlockersTable = ({ item }: { item: ExtraMaterial }) => {
    const orderBlockers = [OrderBlockers.ZONE, OrderBlockers.GRV, OrderBlockers.GNV] as string[]

    const blockers = [...item.Bloqueios].sort(
      (a, b) => orderBlockers.indexOf(a.Tipo) - orderBlockers.indexOf(b.Tipo),
    )

    const headItems = blockers.map((blocker) => {
      return { columnName: blocker.Tipo, label: blocker.Tipo }
    })

    const blockersHead: HeadItemType[] = [
      { columnName: 'bloqueio', label: 'Bloqueio' },
      ...headItems,
    ]

    const blockersBody = [
      {
        content: [
          <S.ColContent
            key={`label-bloq-cotas-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            <S.RowText type={'neutral'}>Cota digitada</S.RowText>
            <S.RowText small type={'neutral'}>
              Saldo da cota
            </S.RowText>
          </S.ColContent>,
          ...blockers.map((b) => (
            <S.ColContent
              key={`${b.Tipo}-bloq-cotas-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            >
              <S.RowText type={b.Porcentagem >= LIMIT_AMOUNT ? 'negative' : 'neutral'}>
                {formatToPTBRNumber(b.Porcentagem, false)}%
              </S.RowText>
              <S.RowText small type={b.Porcentagem >= LIMIT_AMOUNT ? 'negative' : 'neutral'}>
                {numberToDecimalString(b.SaldoCota)}
              </S.RowText>
            </S.ColContent>
          )),
        ],
        id: `bloq-cotas-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`,
      },
    ]

    return (
      <ContentTooltip
        content={<Table bodyData={blockersBody} headData={blockersHead} rowHeight={48} />}
        key={`cota-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
        title={
          <span>
            <strong>{item.CodigoMaterial}</strong> • {item.DescricaoMateial}
          </span>
        }
      >
        <S.RowText type={item.CotaDigitada >= LIMIT_AMOUNT ? 'negative' : 'neutral'}>
          {formatToPTBRNumber(item.CotaDigitada, false)}%
        </S.RowText>
      </ContentTooltip>
    )
  }

  const ColorSizesTable = ({ item, cor }: { item: ExtraMaterial; cor: Cor }) => {
    const headItems = [
      { columnName: 'numeracao', label: 'Numeração' },
      ...cor.Tamanhos.map((t) => {
        return { columnName: t.Numeracao, label: t.Numeracao }
      }),
    ]

    const tableBody = [
      {
        content: [
          <S.ColContent
            key={`Label-vol-vol-${cor.Codigo}${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            <S.RowText type={'neutral'}>Volume</S.RowText>
            <S.RowText small type={'neutral'}>
              Estoque
            </S.RowText>
          </S.ColContent>,
          ...cor.Tamanhos.map((t) => (
            <S.ColContent
              key={`${t.Numeracao}-vol-vol-${cor.Codigo}${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            >
              <S.RowText type={'neutral'}>{numberToDecimalString(t.VolumeSocilitado)}</S.RowText>
              <S.RowText
                small
                type={t.QuantidadeEstoque <= MIN_STOCK_AMOUNT ? 'negative' : 'neutral'}
              >
                {numberToDecimalString(t.QuantidadeEstoque)}
              </S.RowText>
            </S.ColContent>
          )),
        ],
        id: `tam-cor-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`,
      },
    ]

    return (
      <ContentTooltip
        content={<Table bodyData={tableBody} headData={headItems} rowHeight={48} />}
        key={`vol-${cor.Codigo}${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
        title={
          <S.TitleRow>
            <span>
              <strong>{item.CodigoMaterial}</strong> • {item.DescricaoMateial}
            </span>{' '}
            <span>
              <strong>Regra de exceção</strong>: {item.RegraExcecao}
            </span>
          </S.TitleRow>
        }
      >
        <S.RowText type={'neutral'}>{numberToDecimalString(cor.VolumeSolicitado)}</S.RowText>
      </ContentTooltip>
    )
  }

  const tableHead: HeadItemType[] = [
    { columnName: '', label: '' },
    { columnName: 'Pedido', label: 'Pedido' },
    { columnName: 'Material', label: 'Material' },
    { columnName: 'VolumeSolicitado', label: 'Vol. solicitado' },
    { columnName: 'MesCota', label: 'Mês cota' },
    { columnName: 'Bloqueio', label: 'Bloqueio' },
    { columnName: 'PorcentagemCotaDigitada', label: '% de cota digitada' },
    { columnName: 'SaldoCota', label: 'Saldo da cota' },
    { columnName: 'Estoque', label: 'Estoque' },
    { columnName: 'RegraExcecao', label: 'Regra de exceção' },
    { columnName: 'Tecnologia', label: 'Tecnologia' },
    { columnName: 'SemCluster', label: 'S/ Cluster' },
    { columnName: 'EscritorioVenda', label: 'Escritório' },
    { columnName: 'Executivo', label: 'Executivo' },
    { columnName: 'Cliente', label: 'Cliente' },
    { columnName: 'DataSolicitacao', label: 'Data de solicitação' },
    { columnName: 'Acoes', label: 'Ações' },
  ]

  const setChecked = (checked: boolean, id?: string) => {
    if (id) {
      const newMaterialsList = materialsList.map((item) => {
        if (
          `${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}` ===
          id
        ) {
          return { ...item, checked }
        }
        return item
      })
      dispatch(setMaterialList(newMaterialsList))
    } else {
      const newMaterialsList = materialsList.map((item) => {
        return { ...item, checked }
      })
      dispatch(setMaterialList(newMaterialsList))
    }
  }

  const setExpanded = (id: string) => {
    const newMaterialsList = materialsList.map((item) => {
      if (
        `${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}` ===
        id
      ) {
        return { ...item, expanded: !item?.expanded }
      }
      return item
    })
    dispatch(setMaterialList(newMaterialsList))
  }

  const getMaterialsData = () => {
    const tableData = []
    const emptyTd = new Array(tableHead.length - 10).fill('')

    const materialsData = payload.map((item) => {
      return {
        checked: item.checked ?? false,
        content: [
          <S.RowIcon
            key={`icons-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            {' '}
            {item.TipoCritica !== CriticismType.STOCK && (
              <Tooltip arrow placement="top" title={'Indisponível por cota'}>
                <span>
                  <Icon color={theme.colors.alert.warning.dark} icon="report" size="15" />
                </span>
              </Tooltip>
            )}
            {item.TipoCritica !== CriticismType.QUOTA && (
              <Tooltip arrow placement="top" title={'Indisponível por estoque'}>
                <span>
                  <Icon color={theme.colors.alert.warning.dark} icon="box" size="15" />
                </span>
              </Tooltip>
            )}
          </S.RowIcon>,
          <S.RowText
            key={`ped-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            type={'neutral'}
          >
            {item.NumeroPedido}
          </S.RowText>,
          <S.ColContent
            key={`cod-material-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            <S.RowIcon>
              <S.RowText type={'neutral'}>{item.CodigoMaterial}</S.RowText>
              {item.TemRestricaoAnalise === TypesValidations.YES && (
                <Tooltip
                  arrow
                  placement="top"
                  title={'Produto recentemente reprovado com restrição'}
                >
                  <span>
                    <Icon color={theme.colors.alert.danger[40]} icon="sync-problem" size="15" />
                  </span>
                </Tooltip>
              )}
            </S.RowIcon>
            <Typography design="hav-default-04-text">
              {toAllFirstLetterUpper(item.DescricaoMateial)}
            </Typography>
          </S.ColContent>,
          <S.RowText
            key={`vol-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            type={'neutral'}
          >
            {numberToDecimalString(item.VolumeSolicitado)}
          </S.RowText>,
          <S.RowText
            key={`mes-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            type={'neutral'}
          >
            {item.MesCota}
          </S.RowText>,
          <S.RowText
            key={`bloq-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            type={'neutral'}
          >
            {item.TipoBLoqueio}
          </S.RowText>,
          <BlockersTable
            item={item}
            key={`cota-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          />,
          <S.RowText
            key={`sal-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            type={'neutral'}
          >
            {numberToDecimalString(item.SaldoCota)}
          </S.RowText>,
          <S.RowText
            key={`est-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            type={'neutral'}
          >
            {numberToDecimalString(item.VolumeEstoque)}
          </S.RowText>,
          <S.RowText
            key={`reg-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            type={'neutral'}
          >
            {item.RegraExcecao}
          </S.RowText>,
          <Tooltip
            arrow
            key={`tec-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            placement="top"
            title={`Produção ≈ ${item.TempoProducaoTecnologia} dias`}
          >
            <S.RowText type={'neutral'}>{item.Tecnologia}</S.RowText>
          </Tooltip>,
          <S.RowText
            key={`clu-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            type={'neutral'}
          >
            {item.TemCluster.toLocaleUpperCase() === YesNoNotApplicable.YES_UPPERCASE
              ? YesNoNotApplicable.YES
              : YesNoNotApplicable.NO}
          </S.RowText>,
          <S.RowText
            key={`exe-${item.EscritorioVenda}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            type={'neutral'}
          >
            {item.EscritorioVenda}
          </S.RowText>,
          <S.RowText
            key={`exe-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            type={'neutral'}
          >
            {item.NomeExecutivo}
          </S.RowText>,
          <S.RowText
            key={`clie-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            type={'neutral'}
          >
            {item.CodigoCliente}
          </S.RowText>,
          <S.RowText
            key={`dat-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
            type={'neutral'}
          >
            {formatDate(item.DataSolicitacao, 'DD/MM')}
          </S.RowText>,
          <S.ActionsCell
            key={`action-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
          >
            <Tooltip
              arrow
              placement="top"
              title={shouldDisable(ACTIONS_SCREENS.APPROVE) ? '' : 'Aprovar Negociação'}
            >
              <S.ApproveButton
                disabled={shouldDisable(ACTIONS_SCREENS.APPROVE)}
                onClick={() => handleConfirmation(false, item)}
              >
                <Icon color={theme.colors.neutral.white} icon="task-alt" size="15" />
              </S.ApproveButton>
            </Tooltip>

            <Tooltip
              arrow
              placement="top"
              title={shouldDisable(ACTIONS_SCREENS.DISMISS) ? '' : 'Reprovar Negociação'}
            >
              <S.RejectButton
                disabled={shouldDisable(ACTIONS_SCREENS.DISMISS)}
                onClick={() => handleConfirmation(true, item)}
              >
                <Icon color={theme.colors.neutral.white} icon="cancel" size="15" />
              </S.RejectButton>
            </Tooltip>
          </S.ActionsCell>,
        ],
        expanded: item.expanded ?? false,
        id: `${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`,
        onDoubleClick: () => {
          return
        },
        subRows: item.Cores?.length
          ? item.Cores.map((cor) => {
              return {
                content: [
                  <S.RowText
                    key={`label-${cor.Codigo}${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
                    type={'neutral'}
                  >
                    <span id="colorText">Cor</span>
                  </S.RowText>,
                  <S.RowText
                    key={`cod-${cor.Codigo}${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
                    type={'neutral'}
                  >
                    {cor.Codigo}
                  </S.RowText>,
                  <S.RowText
                    key={`des-${cor.Codigo}${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
                    type={'neutral'}
                  >
                    {cor.Descricao}
                  </S.RowText>,
                  <ColorSizesTable
                    cor={cor}
                    item={item}
                    key={`vol-${cor.Codigo}${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
                  />,
                  '',
                  '',
                  '',
                  '',
                  <S.RowText
                    key={`est-q-${cor.Codigo}${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`}
                    type={'neutral'}
                  >
                    {numberToDecimalString(cor.QuantidadeEstoque)}
                  </S.RowText>,
                  ...emptyTd,
                ],
                id: `${cor.Codigo}-${item.CodigoCliente}-${item.CodigoMaterial}-${item.NumeroPedido}-${item.NumeroLinha}`,
              }
            })
          : undefined,
      }
    })

    tableData.push(...materialsData)

    return tableData
  }

  const VolumeTableFooter = () => {
    return (
      <S.FooterDiv>
        <S.FooterRow>
          <Pagination
            currentPage={pagination.currentPage}
            itemsPerPage={pagination.pageSize}
            numberOfItems={pagination.totalItems}
            numberOfPages={pagination.totalPages}
            setCurrentPage={handlePageChange}
          />
          <S.FooterActions>
            <Tooltip
              arrow
              placement="top"
              title={
                !getCheckedExtraVolume().length ? (
                  <span>
                    Selecione uma ou mais linhas na lista <br />
                    para executar ações em massa
                  </span>
                ) : (
                  ''
                )
              }
            >
              <div>
                <SlimButton
                  content="Reprovar"
                  disabled={
                    shouldDisable(ACTIONS_SCREENS.DISMISS) || !getCheckedExtraVolume().length
                  }
                  icon={<Icon icon="cancel" size="15" />}
                  onClick={() => {
                    handleConfirmation(true)
                  }}
                  variant="danger"
                />
              </div>
            </Tooltip>
            <Tooltip
              arrow
              placement="top"
              title={
                !getCheckedExtraVolume().length ? (
                  <span>
                    Selecione uma ou mais linhas na lista <br />
                    para executar ações em massa
                  </span>
                ) : (
                  ''
                )
              }
            >
              <div>
                <SlimButton
                  content="Aprovar"
                  disabled={
                    shouldDisable(ACTIONS_SCREENS.APPROVE) || !getCheckedExtraVolume().length
                  }
                  icon={<Icon icon="task-alt" size="15" />}
                  onClick={() => handleConfirmation(false)}
                  variant="success"
                />
              </div>
            </Tooltip>
          </S.FooterActions>
        </S.FooterRow>
      </S.FooterDiv>
    )
  }

  return (
    <>
      <Table
        actionColumns={[16]}
        bodyData={getMaterialsData()}
        checkboxSize="small"
        hasCheckbox
        headData={tableHead}
        iconColumns={[0]}
        rowHeight={40}
        setChecked={setChecked}
        toggleExpand={setExpanded}
      />
      {getMaterialsData().length > 0 && <VolumeTableFooter />}
      {showModal && (
        <ModalConfirmation
          criticType={critic}
          handleContinue={handleConfirmationAction}
          handleReturnAction={() => setShowModal(false)}
          isOpen={showModal}
          isReject={isReject}
          itens={actionItems}
          setIsOpen={setShowModal}
        />
      )}
    </>
  )
}

export default VolumeTable
